import { apiClient } from '@api/apiClient';
import {
  DatePicker,
  Divider,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  TextField,
} from '@atgfr8/atg-ui-tailwind';
import { DeleteIcon, PlusIcon } from '@atgfr8/icons';
import { Button, Notification } from '@components';
import { IntegerTimeInput } from '@components/IntegerTimeInput/IntegerTimeInput';
import { FeatureFlags } from '@constants/featureFlags';
import { createForm } from '@felte/solid';
import { useNavigate } from '@solidjs/router';
import { isFeatureFlagEnabled } from '@store/featureFlags/store';
import { closeModal } from '@store/modals';
import { copyOrder, getLocationTimezone, orderStore } from '@store/orders';
import { IOrderViewModel } from '@typeDefinitions/orderTypes';
import { LastUsedTopStopModel } from '@typeDefinitions/stopTypes';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { printError } from '@utils/printUtils';
import { routeOrderDetails } from '@utils/routeOrderDetails';
import isEmpty from 'lodash/isEmpty';
import { DateTime } from 'luxon';
import { Index, Show, createSignal, onMount } from 'solid-js';
import { buildChangeTrackingRequestConfig } from '@changeTracking/utils/buildChangeTrackingHeaders';
import { CHANGE_TRACKING_SOURCES } from '@changeTracking/utils/changeTrackingRegistry';

type formItems = {
  referenceNumber: string;
  pickUpDate: string;
  pickUpTimezone?: string;
  pickUpTime?: string;
  pickUpNumber?: string;
  dropDate?: string;
  dropTime?: string;
  dropNumber?: string;
  dropTimezone?: string;
  orderId: number;
  pickUpBeginTimeRange?: number | null;
  pickUpEndTimeRange?: number | null;
  pickUpComment?: string | null;
  dropBeginTimeRange?: number | null;
  dropEndTimeRange?: number | null;
  dropComment?: string | null;
};
type formState = { lineItems: formItems[] };
const defaultVals = {
  referenceNumber: '',
  pickUpDate: '',
  pickUpTime: '',
  pickUpNumber: '',
  pickUpTimezone: '',
  dropDate: '',
  dropTime: '',
  dropNumber: '',
  dropTimezone: '',
  orderId: 0,
  pickUpBeginTimeRange: null,
  pickUpEndTimeRange: null,
  pickUpComment: null,
  dropBeginTimeRange: null,
  dropEndTimeRange: null,
  dropComment: null,
};

export const copyMultipleModalId = 'CopyMultipleModal';

export const CopyMultipleModal = () => {
  const navigate = useNavigate();
  const [apiResponseError, setApiResponseError] = createSignal<string | null>(
    null,
  );
  const [pickUpTimezone, setPickUpTimezone] = createSignal<string | null>(null);
  const [dropOffTimezone, setDropOffTimezone] = createSignal<string | null>(
    null,
  );

  const { form, setFields, data } = createForm<formState>({
    onSubmit: async () => {
      for (let ind = 0; ind < data().lineItems.length; ind++) {
        const lineItem = data().lineItems[ind];
        const order = await copyOrder(true);
        if (order.loads.length > 0 && order.loads[0].stops) {
          const destIndex = order.loads[0].stops.length - 1;
          order.referenceNumber = lineItem.referenceNumber;
          order.loads[0].stops[0].stopDateTime = lineItem.pickUpDate;
          order.loads[0].stops[0].referenceNum = lineItem.pickUpNumber;
          order.loads[0].stops[destIndex].stopDateTime = lineItem.dropDate;
          order.loads[0].stops[destIndex].referenceNum = lineItem.dropNumber;
          order.loads[0].stops[destIndex].time = lineItem.dropTime;

          if (!isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()) {
            order.loads[0].stops[0].time = lineItem.pickUpTime;
          } else {
            order.loads[0].stops[0].timezone =
              lineItem.pickUpTimezone ?? pickUpTimezone();
            order.loads[0].stops[0].beginTimeRange =
              lineItem.pickUpBeginTimeRange ?? null;
            order.loads[0].stops[0].endTimeRange =
              lineItem.pickUpEndTimeRange ?? null;
            order.loads[0].stops[0].timeComments = lineItem.pickUpComment;
            order.loads[0].stops[destIndex].timezone =
              lineItem.dropTimezone ?? dropOffTimezone();
            order.loads[0].stops[destIndex].beginTimeRange =
              lineItem.dropBeginTimeRange ?? null;
            order.loads[0].stops[destIndex].endTimeRange =
              lineItem.dropEndTimeRange ?? null;
            order.loads[0].stops[destIndex].timeComments = lineItem.dropComment;
          }

          try {
            const response: IOrderViewModel = await apiClient.post(
              'order',
              order,
              buildChangeTrackingRequestConfig([
                CHANGE_TRACKING_SOURCES.orderDetails.orderHeaderCopyOrder,
              ]),
            );
            setFields(`lineItems.${ind}.orderId`, response.id);
          } catch (error) {
            const errorResponse = error as [{ errorMessage: string }];
            const errorMessage = errorResponse
              .map((errorObj) => errorObj.errorMessage)
              .join(' , ');
            setApiResponseError(errorMessage);
          }
        }
      }
    },
    initialValues: {
      lineItems: [defaultVals],
    },
  });

  onMount(() => {
    if (
      orderStore.order.loads.length > 0 &&
      orderStore.order.loads[0].stops !== undefined
    ) {
      const origin = orderStore.order.loads[0].stops[0] as
        | LastUsedTopStopModel
        | undefined;
      const dropoff = orderStore.order.loads[0].stops[
        orderStore.order.loads[0].stops.length - 1
      ] as LastUsedTopStopModel | undefined;

      if (!!origin && isEmpty(origin.timezone)) {
        getLocationTimezone(origin.countryCode ?? 'US', origin.zip)
          .then((timezone) => {
            if (timezone !== undefined) {
              setPickUpTimezone(timezone);
            } else {
              setPickUpTimezone('CST');
            }
          })
          .catch(printError);
      } else if (!!origin && !isEmpty(origin.timezone)) {
        setPickUpTimezone(origin.timezone ?? 'CST');
      }

      if (!!dropoff && isEmpty(dropoff.timezone)) {
        getLocationTimezone(dropoff.countryCode ?? 'US', dropoff.zip)
          .then((timezone) => {
            if (timezone !== undefined) {
              setDropOffTimezone(timezone);
            } else {
              setDropOffTimezone('CST');
            }
          })
          .catch(printError);
      } else if (!!dropoff && !isEmpty(dropoff.timezone)) {
        setPickUpTimezone(dropoff.timezone ?? 'CST');
      }
    }
  });

  return (
    <form ref={form}>
      <div class="flex justify-center mb-2">
        <div class="font-semibold text-xl">Copy Order</div>
      </div>
      <Notification
        type="error"
        text={
          'Do not use this tool to copy loads with multiple stops or if there is more than one carrier'
        }
        style={{ 'margin-bottom': '8px' }}
      />
      {orderStore.order.loads.length > 1 && (
        <div style={{ padding: '5px' }}>
          <Notification
            type="error"
            text={'Cannot copy an order with multiple carriers'}
          />
        </div>
      )}
      {orderStore.order.loads.length >= 1 &&
        (orderStore.order.loads[0].stops?.length ?? 0) > 2 && (
          <div style={{ padding: '5px' }}>
            <Notification
              type="error"
              text={'Cannot copy an order with multiple stops'}
            />
          </div>
        )}
      <div>
        <Divider class="my-2! h-[2px]!" orientation="horizontal" />
      </div>
      <div>
        <div class="text-xl font-medium">Errors</div>
        <Show when={apiResponseError()}>
          <Notification type="error" text={apiResponseError()} />
        </Show>
      </div>
      <div class="overflow-auto">
        <Table>
          <TableHead>
            <TableRow>
              <TableHeaderCell class="text-black!">Delete</TableHeaderCell>
              <TableHeaderCell class="text-black!">Reference #</TableHeaderCell>
              <TableHeaderCell class="text-black!">
                Pick Up Date *
              </TableHeaderCell>
              <Show when={!isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}>
                <TableHeaderCell class="text-black!">
                  Pick Up Time
                </TableHeaderCell>
              </Show>
              <Show when={isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}>
                {/* <Show when={isEmpty(pickUpTimezone())}>
                  <TableCell>PU Time Zone</TableCell>
                </Show> */}
                <TableHeaderCell class="text-black!">
                  PU Begin Time
                </TableHeaderCell>
                <TableHeaderCell class="text-black!">
                  PU End Time
                </TableHeaderCell>
                <TableHeaderCell class="text-black!">Comment</TableHeaderCell>
              </Show>
              <TableHeaderCell class="text-black!">Pick Up #</TableHeaderCell>
              <TableHeaderCell class="text-black!">Drop Date *</TableHeaderCell>
              <Show when={!isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}>
                <TableHeaderCell class="text-black!">Drop Time</TableHeaderCell>
              </Show>
              <Show when={isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}>
                {/* <Show when={isEmpty(pickUpTimezone())}>
                  <TableCell>DO Time Zone</TableCell>
                </Show> */}
                <TableHeaderCell class="text-black!">
                  DO Begin Time
                </TableHeaderCell>
                <TableHeaderCell class="text-black!">
                  DO End Time
                </TableHeaderCell>
                <TableHeaderCell class="text-black!">Comment</TableHeaderCell>
              </Show>
              <TableHeaderCell class="text-black!">Drop #</TableHeaderCell>
              <TableHeaderCell class="text-black!">Order #</TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            <Index each={data().lineItems}>
              {(_, index) => {
                return (
                  <TableRow>
                    <TableCell>
                      <IconButton
                        dataTestId="delete-copy-multiple-line-item-button"
                        onClick={() => {
                          setFields('lineItems', [
                            ...data().lineItems.slice(0, index),
                            ...data().lineItems.slice(index + 1),
                          ]);
                        }}
                      >
                        <DeleteIcon class="text-[#B00020] cursor-pointer" />
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <TextField
                        label=""
                        onChange={(e: string) => {
                          setFields(`lineItems.${index}.referenceNumber`, e);
                        }}
                        dataTestId={`reference-number-textInput-${index}`}
                      />
                    </TableCell>
                    <TableCell>
                      <DatePicker
                        required
                        value={data().lineItems[index].pickUpDate}
                        handleChange={(value: string) => {
                          setFields(
                            `lineItems.${index}.pickUpDate`,
                            DateTime.fromISO(value).toFormat('yyyy-MM-dd'),
                          );
                        }}
                        dataTestId={`pick-up-date-datepicker-${index}`}
                      />
                    </TableCell>
                    <Show
                      when={!isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}
                    >
                      <TableCell>
                        <TextField
                          label=""
                          onChange={(e: string) => {
                            setFields(`lineItems.${index}.pickUpTime`, e);
                          }}
                          dataTestId={`pick-up-time-textInput-${index}`}
                        />
                      </TableCell>
                    </Show>
                    <Show
                      when={isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}
                    >
                      <TableCell>
                        <IntegerTimeInput
                          placeholder="Select Begin Time"
                          onChange={(value) => {
                            setFields(
                              `lineItems.${index}.pickUpBeginTimeRange`,
                              value,
                            );
                          }}
                          value={data().lineItems[index].pickUpBeginTimeRange}
                        />
                      </TableCell>
                      <TableCell>
                        <IntegerTimeInput
                          placeholder="Select End Time"
                          onChange={(value) => {
                            setFields(
                              `lineItems.${index}.pickUpEndTimeRange`,
                              value,
                            );
                          }}
                          value={data().lineItems[index].pickUpEndTimeRange}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          label="Pick Up Time Comment"
                          onChange={(value: string) => {
                            setFields(
                              `lineItems.${index}.pickUpComment`,
                              value,
                            );
                          }}
                          value={data().lineItems[index].pickUpComment ?? ''}
                          dataTestId={`pick-up-time-comment-textareafield-${index}`}
                        />
                      </TableCell>
                    </Show>
                    <TableCell>
                      <TextField
                        label=""
                        onChange={(e: string) => {
                          setFields(`lineItems.${index}.pickUpNumber`, e);
                        }}
                        dataTestId={`pick-up-number-textInput-${index}`}
                      />
                    </TableCell>
                    <TableCell>
                      <DatePicker
                        required
                        value={data().lineItems[index].dropDate}
                        handleChange={(value: string) => {
                          setFields(
                            `lineItems.${index}.dropDate`,
                            DateTime.fromISO(value).toFormat('yyyy-MM-dd'),
                          );
                        }}
                        dataTestId={`drop-date-datepicker-${index}`}
                      />
                    </TableCell>
                    <Show
                      when={!isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}
                    >
                      <TableCell>
                        <TextField
                          label=""
                          onChange={(e: string) => {
                            setFields(`lineItems.${index}.dropTime`, e);
                          }}
                          dataTestId={`drop-time-textInput-${index}`}
                        />
                      </TableCell>
                    </Show>
                    <Show
                      when={isFeatureFlagEnabled(FeatureFlags.MilitaryTime)()}
                    >
                      <TableCell>
                        <IntegerTimeInput
                          placeholder="Select Start Time"
                          onChange={(value) => {
                            setFields(
                              `lineItems.${index}.dropBeginTimeRange`,
                              value,
                            );
                          }}
                          value={data().lineItems[index].dropBeginTimeRange}
                        />
                      </TableCell>
                      <TableCell>
                        <IntegerTimeInput
                          placeholder="Select End Time"
                          onChange={(value) => {
                            setFields(
                              `lineItems.${index}.dropEndTimeRange`,
                              value,
                            );
                          }}
                          value={data().lineItems[index].dropEndTimeRange}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          label="Drop Time Comment"
                          onChange={(value: string) => {
                            setFields(`lineItems.${index}.dropComment`, value);
                          }}
                          value={data().lineItems[index].dropComment ?? ''}
                          dataTestId={`drop-time-comment-textareafield-${index}`}
                        />
                      </TableCell>
                    </Show>
                    <TableCell>
                      <TextField
                        label=""
                        onChange={(e: string) => {
                          setFields(`lineItems.${index}.dropNumber`, e);
                        }}
                        dataTestId={`drop-number-textInput-${index}`}
                      />
                    </TableCell>
                    <TableCell>
                      <Button
                        label={
                          data().lineItems[index].orderId
                            ? data().lineItems[index].orderId.toString()
                            : ''
                        }
                        disabled={data().lineItems[index].orderId === 0}
                        sx={{ 'min-height': '50px', 'min-width': '100px' }}
                        onClick={() => {
                          const isNative = routeOrderDetails(
                            data().lineItems[index].orderId,
                          );
                          if (isNative) {
                            navigate(
                              `/order/details/${
                                data().lineItems[index].orderId
                              }`,
                            );
                            closeModal(copyMultipleModalId);
                          }
                        }}
                        dataTestId={formatDataTestId(
                          data().lineItems[index].orderId
                            ? data().lineItems[index].orderId.toString()
                            : '',
                          'button',
                        )}
                      />
                    </TableCell>
                  </TableRow>
                );
              }}
            </Index>
            <TableRow>
              <TableCell>
                <IconButton
                  size="sm"
                  dataTestId="add-copy-multiple-line-item-button"
                  onClick={() => {
                    setFields('lineItems', [...data().lineItems, defaultVals]);
                  }}
                >
                  <PlusIcon />
                </IconButton>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
      <Button
        sx={{
          width: '100%',
        }}
        class="mt-[15px]"
        disabled={
          orderStore.order.loads.length > 1 ||
          (orderStore.order.loads.length >= 1 &&
            (orderStore.order.loads[0].stops?.length ?? 0) > 2)
        }
        type="submit"
        variant="contained"
        label="Create"
        dataTestId={formatDataTestId('Create', 'button')}
      />
    </form>
  );
};
